import { useGameController, useGameDirective } from "@/components/game/GameUIContext.js";
import Image from "next/image";
import crown from "@rosette/assets/src/crown.svg";
import rightCandyCane from "@rosette/assets/src/game/holiday/candy_cane_right.png";
import React, { useRef } from "react";
import { EndGameState } from "@rosette/ts/src/royalur/engine/state/EndGameState.js";
import { useGameTheme } from "@/components/game/theme/GameThemeContext.js";
import { GameThemeType } from "@rosette/ts/src/business/game/theme/GameThemeType.js";
import type { LobbyPlayer } from "@rosette/ts/src/business/lobby/LobbyPlayer.js";
import { BotLobbyPlayer } from "@rosette/ts/src/business/lobby/BotLobbyPlayer.js";
import { BotType } from "@rosette/ts/src/business/game/BotType.js";
import type { PlayerType } from "@rosette/ts/src/royalur/model/PlayerType.js";
import { HumanLobbyPlayer } from "@rosette/ts/src/business/lobby/HumanLobbyPlayer.js";
import { useRune } from "@rosette/react_utils/src/useRune.js";
import { PandaLooking } from "@/components/game/menu/game_end/PandaLooking.js";
import { cn } from "@rosette/ts/src/util/cn.js";
import { GameEndReason } from "@rosette/ts/src/business/game/GameEndReason.js";


interface GameEndBannerState {
    endReason: GameEndReason;
    winner: LobbyPlayer | null;
    loser: LobbyPlayer | null;
    light: LobbyPlayer;
    dark: LobbyPlayer;
    yourPlayer: PlayerType | null;
}


interface GameEndBannerProps {
    visible: boolean;
    state: GameEndBannerState;
    theme: GameThemeType;
}

export function GameEndBanner({
    visible, state, theme,
}: GameEndBannerProps) {
    const {
        endReason,
        winner,
        loser,
        light,
        dark,
        yourPlayer,
    } = state;

    const winnerData = winner?.getGameData();
    const loserData = loser?.getGameData();
    if ((winner || loser) && (!winnerData || !loserData))
        throw new Error("Missing player game data");

    const hasWinner = (!!winner && endReason !== GameEndReason.LEFT_BEFORE_BEGIN);
    const didYourPlayerWin = (winnerData?.getPlayerType() === yourPlayer);
    const pandaWasPlaying = (
        (light instanceof BotLobbyPlayer && light.getBotType() === BotType.PANDA)
        || (dark instanceof BotLobbyPlayer && dark.getBotType() === BotType.PANDA)
    );

    const isHolidayTheme = (theme === GameThemeType.HOLIDAY);
    const hasWinMenu = !!winner;

    const bothPlayersAreAnonymous = (
        (light instanceof HumanLobbyPlayer && light.isAnonymous())
        && (dark instanceof HumanLobbyPlayer && dark.isAnonymous())
    );

    let winnerName: string | null = null;
    let loserName: string | null = null;

    if (winner && loser && winnerData && loserData) {
        winnerName = winner.getLongName();
        loserName = loser.getLongName();
        if (bothPlayersAreAnonymous) {
            winnerName = winnerData.getPlayerType().getName();
            loserName = loserData.getPlayerType().getName();
        } else if (loser instanceof HumanLobbyPlayer && yourPlayer !== null && !didYourPlayerWin) {
            loserName = "you";
        }
    }

    let title: string | null = null;
    let subtitle: string | null = null;
    if (endReason.requiresPlayer()) {
        if (!winner || !loser || !winnerData || !loserData)
            throw new Error("Missing winner or loser while end reason is win");

        title = winnerName;
        if (endReason === GameEndReason.WIN) {
            subtitle = `defeated ${loserName}${didYourPlayerWin ? "!" : ""}`;

        } else if (endReason === GameEndReason.RESIGNATION) {
            subtitle = `won as ${loserName} resigned${didYourPlayerWin ? "!" : ""}`;

        } else if (endReason === GameEndReason.LEFT) {
            subtitle = `won as ${loserName} stopped playing`;

        } else if (endReason === GameEndReason.LEFT_BEFORE_BEGIN) {
            title = loserName;
            subtitle = `never started playing`;

        } else {
            subtitle = `won, ${loserName} lost`;
        }
    } else if (endReason === GameEndReason.SERVER) {
        title = "Abandoned";
        subtitle = "game was never finished";
    }

    return (
        <div className="relative mt-[3em] w-full">
            {pandaWasPlaying && (
                <PandaLooking visible={visible} />
            )}

            <div className={cn(
                "shadow-lg",
                hasWinMenu ? cn(
                    "relative p-4 text-center pointer-events-auto",
                    "border-b-4 border-b-shade rounded-2xl",
                    isHolidayTheme
                        ? "text-on-success bg-success"
                        : cn(
                            "text-on-celebration bg-celebration",
                            "bg-linear-to-b/srgb from-celebration to-celebration-strong",
                        ),
                ) : null,
            )}>
                {/* Only show a crown if the game has a winner. */}
                {hasWinner && (
                    <>
                        {!isHolidayTheme && (
                            <div className={cn(
                                "animate-wiggle motion-reduce:animate-none absolute -top-[3.25em] -left-[2.2em] w-[6em] h-auto",
                                "brightness-105 drop-shadow-lg",
                            )}>
                                <Image className="w-[6em] h-auto" src={crown} alt="Crown" />
                            </div>
                        )}

                        {isHolidayTheme && (
                            <div className={cn(
                                "absolute top-1/2 left-0 h-[12em] w-auto",
                                "-translate-x-[10%] -translate-y-[52.5%] -rotate-20",
                            )}>
                                <Image className="h-[12em] w-auto" src={rightCandyCane} alt="Candy Cane" />
                            </div>
                        )}
                    </>
                )}

                <div className={hasWinMenu ? "text-[length:calc(var(--game-text)*2.667)] leading-[calc(2.5/2.25)] font-bold" : undefined}>
                    <div className={hasWinMenu ? "relative inline-block" : undefined}>
                        {title}
                        {hasWinner && (
                            <span className={cn(
                                "animate-shine motion-reduce:hidden absolute left-0 top-0",
                                isHolidayTheme ? "text-success-soft" : "text-accent-soft",
                            )}>
                                {title}
                            </span>
                        )}
                    </div>
                </div>
                <div className={hasWinMenu ? "text-3xl" : undefined}>
                    {subtitle}
                </div>
            </div>
        </div>
    );
}


interface AutoGameEndBannerProps {
    visible: boolean;
}


export function AutoGameEndBanner({ visible }: AutoGameEndBannerProps) {
    const gameController = useGameController();
    const players = useRune(gameController.getPlayers());
    const status = useRune(gameController.getLobbyStatus());
    const game = useRune(gameController.getGame());
    const currentGameDirective = useGameDirective();
    const theme = useGameTheme();
    const state = currentGameDirective.state;

    // As the menu is fading away, we still want to display the last win state.
    const stateRef = useRef<GameEndBannerState | null>(null);
    if (state instanceof EndGameState) {
        let endReason = status.getEndReason();
        if (endReason === null && game !== null && game.isFinished()) {
            const inferredEndReason = GameEndReason.infer(game);
            endReason = inferredEndReason.endReason;
        } else if (endReason === null) {
            endReason = GameEndReason.WIN;
        }

        let winner: LobbyPlayer | null = null;
        let loser: LobbyPlayer | null = null;
        if (state.hasWinner()) {
            winner = players.getPlayerByType(state.getWinner());
            loser = players.getPlayerByType(state.getLoser());
        }

        const light = players.getLightPlayer();
        const dark = players.getDarkPlayer();
        const yourPlayer = players.getYourPlayer();
        stateRef.current = {
            endReason,
            winner,
            loser,
            light,
            dark,
            yourPlayer,
        };
    } else if (stateRef.current === null) {
        throw new Error("The game has not been won!");
    }

    return (
        <GameEndBanner
            visible={visible}
            state={stateRef.current}
            theme={theme.getThemeType()} />
    );
}
